import type { ComposeModalMode } from '@packages/table-builder/registry/Action';
import { formModeEntityLabel, normalizeFormEngineMode } from './formMode';

/** Maps footer action intent → compose modal operational mode. */
export const COMPOSE_INTENT_TO_MODAL_MODE: Record<string, ComposeModalMode> = {
  edit: 'view',
  save: 'edit',
  add: 'create',
  approve: 'approver',
};

export function normalizeComposeModalMode(mode: ComposeModalMode | undefined): ComposeModalMode | undefined {
  if (mode == null) return mode;
  const normalized = normalizeFormEngineMode(mode);
  if (normalized != null) {
    return normalized;
  }
  return mode;
}

export interface ComposeModalMetaInput {
  mode?: ComposeModalMode;
  footerMode?: ComposeModalMode;
  entity?: string;
  title?: string;
}

export function resolveComposeModalMode(
  meta: ComposeModalMetaInput | undefined,
  primaryIntent?: string,
): ComposeModalMode {
  if (meta?.mode) return normalizeComposeModalMode(meta.mode) ?? meta.mode;
  if (meta?.footerMode) return normalizeComposeModalMode(meta.footerMode) ?? meta.footerMode;
  if (primaryIntent == null) return 'cancel-only';
  return COMPOSE_INTENT_TO_MODAL_MODE[primaryIntent] ?? 'view';
}

export type ComposeEffectiveMetaExtras = {
  formSessionKey?: string;
  composeInstanceKey?: string;
  formSchemaId?: string;
};

/** Canonical meta passed to ControlRenderer — always overwrites raw action meta.mode. */
export function buildComposeEffectiveMeta(
  meta: Record<string, unknown> | undefined,
  currentMode: ComposeModalMode,
  extras?: ComposeEffectiveMetaExtras,
): Record<string, unknown> | undefined {
  if (!meta) return extras ? { mode: currentMode, ...extras } : undefined;
  return { ...meta, mode: currentMode, ...extras };
}

export function resolveComposeModalTitle(
  meta: ComposeModalMetaInput | undefined,
  currentMode: ComposeModalMode,
): string {
  if (!meta) return '';
  // Entity-driven titles follow in-place view→edit (Edit X / View X). Static `title` is for
  // summary-style modals (e.g. participant name) when `entity` is omitted.
  if (meta.entity) {
    const modeLabel = formModeEntityLabel(currentMode);
    return `${modeLabel} ${meta.entity}`;
  }
  if (meta.title != null && meta.title !== '') {
    return meta.title;
  }
  return '';
}
